/**
 * Copy for the paid-traffic landing page (/wcag-audit). Each Google Ads ad
 * group links with `?service=<key>` so the headline matches the search; an
 * absent or unknown value shows the default. Every variant shares one
 * canonical URL (/wcag-audit) so search engines index a single page.
 *
 * Claims here are limited to what the homepage already states about the
 * service and its deliverables.
 */

export type VariantKey = "default" | "testing" | "sebi" | "vpat" | "remediation";

export interface Variant {
  key: VariantKey;
  /** <title> (≤ 60 chars, checked in tests). */
  title: string;
  /** Meta description (≤ 160 chars, checked in tests). */
  description: string;
  eyebrow: string;
  h1: string;
  intro: string;
  /** Service preselected in the form (CRM `service` value). */
  service: string;
}

export const VARIANTS: Record<VariantKey, Variant> = {
  default: {
    key: "default",
    title: "WCAG 2.2 Accessibility Audit in India | AccessBharat",
    description:
      "WCAG 2.2 AA audits of websites, apps and PDFs by IAAP-certified auditors. Every issue mapped to its fix, fee agreed before we start. Reply within 24 hours.",
    eyebrow: "WCAG 2.2 AA audit",
    h1: "WCAG 2.2 accessibility audit for your website, app and PDFs",
    intro:
      "Certified auditors test your digital estate against WCAG 2.2 AA, IS 17802 and GIGW 3.0 — automated scans plus manual keyboard and screen-reader testing — and hand your developers every issue with its fix.",
    service: "wcag_audit",
  },
  testing: {
    key: "testing",
    title: "Accessibility Testing Services in India | AccessBharat",
    description:
      "Manual and automated accessibility testing with keyboard and screen readers, mapped to WCAG 2.2 AA. Severity-rated findings your developers can act on.",
    eyebrow: "Accessibility testing",
    h1: "Accessibility testing with keyboards and screen readers, not just scanners",
    intro:
      "Automated tools miss keyboard traps, confusing screen-reader output and broken focus order. Our auditors test your key user journeys by hand and confirm every automated finding.",
    service: "testing",
  },
  sebi: {
    key: "sebi",
    title: "SEBI Accessibility Audit in India | AccessBharat",
    description:
      "Accessibility audits for SEBI-regulated entities: WCAG 2.2 AA and IS 17802 testing, version-controlled reports for each cycle, and a remediation roadmap.",
    eyebrow: "SEBI accessibility circular",
    h1: "Accessibility audit for SEBI-regulated entities",
    intro:
      "Banks, NBFCs, brokers and listed entities: we audit your websites and apps against WCAG 2.2 AA and IS 17802, and give you version-controlled reports and a remediation roadmap for each reporting cycle.",
    service: "sebi",
  },
  vpat: {
    key: "vpat",
    title: "VPAT and Accessibility Conformance Report | AccessBharat",
    description:
      "VPAT / Accessibility Conformance Reports for SaaS and exporters, based on a real WCAG 2.2 audit — for buyers under ADA, Section 508 and the EAA.",
    eyebrow: "VPAT / ACR",
    h1: "A VPAT your buyers can trust, backed by a real audit",
    intro:
      "Overseas buyers ask for an Accessibility Conformance Report before they sign. We audit your product against WCAG 2.2, then write the VPAT from the evidence — ready for ADA, Section 508 and European Accessibility Act reviews.",
    service: "vpat",
  },
  remediation: {
    key: "remediation",
    title: "Accessibility Remediation Services in India | AccessBharat",
    description:
      "Fix WCAG 2.2 AA issues in code, design and PDFs with a prioritised action plan, then re-test and get a conformance report. Reply within 24 hours.",
    eyebrow: "Accessibility remediation",
    h1: "Fix your accessibility issues — and prove they are fixed",
    intro:
      "Already have an audit, or a list of complaints? We prioritise the issues, fix them in code, design and PDFs alongside your team, then re-test and issue the conformance report.",
    service: "remediation",
  },
};

export function variantFor(service: string | null | undefined): Variant {
  return service && service in VARIANTS && service !== "default"
    ? VARIANTS[service as VariantKey]
    : VARIANTS.default;
}

/** Form options: CRM `service` values with their labels. */
export const SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: "wcag_audit", label: "WCAG audit" },
  { value: "testing", label: "Accessibility testing" },
  { value: "sebi", label: "SEBI accessibility compliance" },
  { value: "vpat", label: "VPAT / Accessibility Conformance Report" },
  { value: "remediation", label: "Remediation (fixing issues)" },
  { value: "pdf", label: "PDF / document accessibility" },
  { value: "other", label: "Something else" },
];

export const TIMELINE_OPTIONS: { value: string; label: string }[] = [
  { value: "lt_1_month", label: "Within a month" },
  { value: "1_3_months", label: "In 1–3 months" },
  { value: "3_6_months", label: "In 3–6 months" },
  { value: "researching", label: "Just researching" },
];

export const INCLUDED = [
  {
    title: "Scope and fee, before we start",
    body: "We crawl your site and count pages and PDF pages first, so the fee is based on what you actually have.",
  },
  {
    title: "Automated plus manual testing",
    body: "WCAG rule checks on every page, then certified auditors test keyboard, focus, screen-reader and zoom behaviour by hand.",
  },
  {
    title: "Developer report",
    body: "Every issue with its WCAG criterion, the affected element, a screenshot and the recommended fix.",
  },
  {
    title: "Executive summary",
    body: "A score, severity breakdown and plain-language summary that leadership and compliance teams can read in minutes.",
  },
  {
    title: "Action plan and Excel export",
    body: "A prioritised remediation roadmap and the full issue list in a spreadsheet your team can assign and track.",
  },
  {
    title: "Indian standards covered",
    body: "WCAG 2.2 AA, IS 17802 and GIGW 3.0 checks, plus a VPAT / ACR when your buyers need one.",
  },
];

export const STEPS = [
  { name: "Tell us what to test", detail: "Share your websites, apps or documents in the form. We reply within 24 hours." },
  { name: "Agree scope and fee", detail: "We count your pages and PDFs and send an engagement letter with a fixed fee." },
  { name: "Audit", detail: "Automated scans and manual assistive-technology testing, every finding rated by severity." },
  { name: "Report and fix", detail: "You get the reports and action plan; we can help your team fix and re-test." },
];

export const LANDING_FAQ = [
  {
    question: "How long does an audit take?",
    answer:
      "A typical audit runs two to four weeks depending on the number of page templates and user journeys. We confirm the timeline in the engagement letter before we start.",
  },
  {
    question: "How is the fee worked out?",
    answer:
      "We crawl your site first and count the pages and PDF pages, then quote a fixed fee for that scope. You see the scope and fee before you commit.",
  },
  {
    question: "Which standards do you test against?",
    answer:
      "WCAG 2.2 Level AA by default, plus IS 17802 and GIGW 3.0 for Indian requirements. We can also test against WCAG 2.1 if your contract or regulator asks for it.",
  },
  {
    question: "Do accessibility overlays or widgets make a site compliant?",
    answer:
      "No. Overlay widgets cannot fix most barriers and can introduce new ones. Compliance comes from fixing the underlying code and content, verified by manual testing.",
  },
  {
    question: "Can you help us fix the issues you find?",
    answer:
      "Yes. We can guide your developers through the action plan, fix code, design and PDFs alongside them, then re-test and issue a conformance report or VPAT.",
  },
];
